<template>
  <v-dialog
    :model-value="modelValue"
    persistent
    scrollable
    max-width="640"
    @update:model-value="(v) => !v && close()"
  >
    <v-card class="result-card">
      <!-- Header (green = granted, red = denied) -->
      <div class="result-header" :class="type">
        <v-icon size="20" color="#fff">{{
          isGranted ? "mdi-check-circle" : "mdi-close-circle"
        }}</v-icon>
        <span class="result-title">{{
          isGranted ? "Granted Intern Request" : "Denied Intern Request"
        }}</span>
        <v-spacer></v-spacer>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          color="#fff"
          @click="close()"
        />
      </div>

      <v-card-text class="result-body">
        <!-- Outcome banner -->
        <div class="outcome-banner" :class="type">
          <v-icon size="16" :color="isGranted ? '#16a34a' : '#dc2626'">{{
            isGranted ? "mdi-check-circle-outline" : "mdi-alert-circle-outline"
          }}</v-icon>
          <span>{{ outcomeText }}</span>
        </div>

        <!-- Request details -->
        <div class="panel-section">
          <div class="panel-section-title">
            <v-icon size="15" color="#16a34a">mdi-information-outline</v-icon>
            Request Details
          </div>

          <div class="detail-row">
            <span class="detail-label">Number of Interns</span>
            <span class="detail-value highlight">{{ numInterns }}</span>
          </div>

          <div class="detail-row" v-if="programIds.length > 0">
            <span class="detail-label">Programs</span>
            <div class="tags-wrap">
              <span class="prog-tag" v-for="id in programIds" :key="id">
                {{ getProgramAbbr(id) }}
              </span>
            </div>
          </div>

          <div class="detail-row">
            <span class="detail-label">Student Batch</span>
            <div class="tags-wrap">
              <span class="prog-tag">{{ batchName }}</span>
            </div>
          </div>
        </div>

        <!-- Required skills -->
        <div class="panel-section" v-if="skills.length > 0">
          <div class="panel-section-title">
            <v-icon size="15" color="#2563eb">mdi-lightbulb-outline</v-icon>
            Required Skills
          </div>
          <div class="skills-wrap">
            <span class="skill-tag" v-for="(s, idx) in skills" :key="idx">
              {{ s }}
            </span>
          </div>
        </div>

        <!-- Granted students -->
        <div class="panel-section" v-if="grantedStudents.length > 0">
          <div class="panel-section-title">
            <v-icon size="15" color="#16a34a">mdi-account-check-outline</v-icon>
            Granted Students
            <span class="section-badge green">{{
              grantedStudents.length
            }}</span>
          </div>
          <div class="student-list">
            <div
              class="student-row green"
              v-for="(s, idx) in grantedStudents"
              :key="'g' + (s.id ?? idx)"
            >
              <div class="student-avatar green-av">{{ s.initials }}</div>
              <span class="student-name">{{ s.name }}</span>
              <span class="student-num">#{{ idx + 1 }}</span>
            </div>
          </div>
        </div>

        <!-- Denied students -->
        <div class="panel-section" v-if="deniedStudents.length > 0">
          <div class="panel-section-title">
            <v-icon size="15" color="#dc2626"
              >mdi-account-remove-outline</v-icon
            >
            Denied Students
            <span class="section-badge red">{{ deniedStudents.length }}</span>
          </div>
          <div class="student-list">
            <div
              class="student-row red"
              v-for="(s, idx) in deniedStudents"
              :key="'d' + (s.id ?? idx)"
            >
              <div class="student-avatar red-av">{{ s.initials }}</div>
              <span class="student-name">{{ s.name }}</span>
              <span class="student-num">#{{ idx + 1 }}</span>
            </div>
          </div>
        </div>

        <!-- Fallback: no granted/denied lists came back, show who was requested -->
        <div
          class="panel-section"
          v-if="
            grantedStudents.length === 0 &&
            deniedStudents.length === 0 &&
            requestedStudents.length > 0
          "
        >
          <div class="panel-section-title">
            <v-icon size="15" color="#7c3aed">mdi-account-group-outline</v-icon>
            Requested Students
            <span class="section-badge">{{ requestedStudents.length }}</span>
          </div>
          <div class="student-list">
            <div
              class="student-row"
              v-for="(s, idx) in requestedStudents"
              :key="'r' + (s.id ?? idx)"
            >
              <div class="student-avatar purple-av">{{ s.initials }}</div>
              <span class="student-name">{{ s.name }}</span>
              <span class="student-num">#{{ idx + 1 }}</span>
            </div>
          </div>
        </div>

        <!-- Remarks -->
        <div class="panel-section">
          <div class="panel-section-title">
            <v-icon size="15" color="#374151">mdi-comment-text-outline</v-icon>
            Remarks
          </div>
          <div v-if="remarks" class="remarks-text">{{ remarks }}</div>
          <div v-else class="remarks-empty">No remarks provided.</div>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <div class="result-footer">
        <v-btn class="footer-cancel-btn" elevation="0" @click="close()">
          Close
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "ViewHTEInternRequestResultDialog",
  props: {
    // v-model: open / closed
    modelValue: { type: Boolean, default: false },
    // The request row from the table
    data: { type: Object, default: null },
    // 'granted' | 'denied'
    type: { type: String, default: "granted" },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      programsList: [],
      batchList: [],
    };
  },
  computed: {
    isGranted() {
      return this.type === "granted";
    },
    req() {
      return this.data || {};
    },
    numInterns() {
      return this.req.num_interns ?? "-";
    },
    programIds() {
      return this.parseList(this.req.programs);
    },
    skills() {
      return this.parseList(this.req.skills);
    },
    batchName() {
      const id = this.req.student_batch_id;
      if (!id) return "-";
      const found = this.batchList.find((b) => b.id == id);
      return found ? found.name : "-";
    },
    remarks() {
      return this.req.remarks || null;
    },

    // ── Student lists ──────────────────────────────────────────────────
    // Adjust these field names if your backend returns them differently.
    grantedStudents() {
      return this.toStudents(
        this.req.granted_students ?? this.req.student_granted,
      );
    },
    deniedStudents() {
      return this.toStudents(
        this.req.denied_students ?? this.req.student_denied,
      );
    },
    requestedStudents() {
      return this.toStudents(this.req.st_data);
    },

    outcomeText() {
      const need = Number(this.req.num_interns) || 0;
      if (this.isGranted) {
        const got = this.grantedStudents.length;
        if (got > 0 && need > 0) {
          return `${got} of ${need} requested interns were granted.`;
        }
        return "This request was granted.";
      }
      if (this.deniedStudents.length > 0) {
        return `This request was denied. ${this.deniedStudents.length} student(s) were declined.`;
      }
      return "This request was denied.";
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.loadLookups();
    },
  },
  methods: {
    async loadLookups() {
      try {
        const [progsRes, batchRes] = await Promise.all([
          this.axiosCall("/program", "GET"),
          this.axiosCall("/student/get-institute-batch-list", "GET"),
        ]);
        this.programsList = progsRes.data || [];
        this.batchList = batchRes.data || [];
      } catch (error) {
        console.error(error);
      }
    },

    getProgramAbbr(id) {
      const found = this.programsList.find((p) => p.id == id);
      return found ? found.abbr : "";
    },

    // Accepts an array, a JSON string of an array, or nothing
    parseList(value) {
      if (Array.isArray(value)) return value;
      if (typeof value === "string" && value.trim() !== "") {
        try {
          const parsed = JSON.parse(value);
          return Array.isArray(parsed) ? parsed : [parsed];
        } catch {
          return [value];
        }
      }
      return [];
    },

    // Turns student objects into { id, name, initials }
    toStudents(value) {
      return this.parseList(value)
        .filter((s) => s && typeof s === "object")
        .map((s) => {
          const fname = s.fname || "";
          const lname = s.lname || "";
          const mid = s.mname ? s.mname[0] + ". " : "";
          return {
            id: s.id,
            name: this.toTitleCase(`${fname} ${mid}${lname}`.trim()),
            initials: `${fname[0] || ""}${lname[0] || ""}`.toUpperCase(),
          };
        });
    },

    close() {
      this.$emit("update:modelValue", false);
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap");

.result-card {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  border-radius: 16px !important;
  overflow: hidden;
}

/* ── Header ── */
.result-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  color: #fff;
}
.result-header.granted {
  background: #16a34a;
}
.result-header.denied {
  background: #dc2626;
}
.result-title {
  font-size: 15px;
  font-weight: 600;
  color: #fff;
}

/* ── Body ── */
.result-body {
  padding: 20px !important;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #f4f6f8;
  max-height: 640px;
  overflow-y: auto;
}

/* ── Outcome banner ── */
.outcome-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid;
}
.outcome-banner.granted {
  background: #f0fdf4;
  border-color: #bbf7d0;
  color: #166534;
}
.outcome-banner.denied {
  background: #fef2f2;
  border-color: #fecaca;
  color: #991b1b;
}

/* ── Sections ── */
.panel-section {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.panel-section-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.section-badge {
  margin-left: auto;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  color: #374151;
  padding: 1px 8px;
}
.section-badge.green {
  background: #dcfce7;
  border-color: #bbf7d0;
  color: #15803d;
}
.section-badge.red {
  background: #fee2e2;
  border-color: #fecaca;
  color: #dc2626;
}

.detail-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f9fafb;
}
.detail-row:last-child {
  border-bottom: none;
}
.detail-label {
  font-size: 12px;
  color: #6b7280;
}
.detail-value {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.detail-value.highlight {
  background: #dcfce7;
  color: #15803d;
  padding: 2px 10px;
  border-radius: 6px;
  border: 1px solid #bbf7d0;
}

.tags-wrap,
.skills-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.prog-tag {
  background: #dbeafe;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 10px;
}
.skill-tag {
  background: #dbeafe;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  padding: 3px 10px;
}

/* ── Students ── */
.student-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.student-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}
.student-row.green {
  background: #f0fdf4;
  border-color: #bbf7d0;
}
.student-row.red {
  background: #fff5f5;
  border-color: #fecaca;
}
.student-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.green-av {
  background: #dcfce7;
  color: #15803d;
}
.red-av {
  background: #fee2e2;
  color: #dc2626;
}
.purple-av {
  background: #ede9fe;
  color: #6d28d9;
}
.student-name {
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.student-num {
  font-size: 11px;
  color: #9ca3af;
  background: #f3f4f6;
  border-radius: 4px;
  padding: 1px 6px;
}

/* ── Remarks ── */
.remarks-text {
  font-size: 13px;
  color: #111827;
  line-height: 1.5;
  white-space: pre-wrap;
}
.remarks-empty {
  font-size: 13px;
  color: #9ca3af;
}

/* ── Footer ── */
.result-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 14px 20px;
  background: #fff;
}
.footer-cancel-btn {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  background: #f3f4f6 !important;
  color: #374151 !important;
  border: 1px solid #e5e7eb !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  height: 38px !important;
  padding: 0 18px !important;
}
</style>
